import Element, { ElementType, RootEntityType } from '@/modules/elements/models/Element'
import { useElementStore } from '@/modules/elements/stores/ElementStore'
import { GeneralApiResponse } from '@/modules/global/models/GeneralApiResponse'
import CreateSpaceRequest from '@/modules/space/models/CreateSpaceRequest'
import DuplicateSpaceRequest from '@/modules/space/models/DuplicateSpaceRequest'
import { RemoveRecipientsRequest } from '@/modules/space/models/InviteRecipientsRequest'
import Space, { DigitalSignatureStatus, SpaceVerificationMode, UpdateSpaceRequest } from '@/modules/space/models/Space'
import SpacePage from '@/modules/space/models/SpacePage'
import Task from '@/modules/task/models/Task'
import { useTaskStore } from '@/modules/task/stores/TaskStore'
import User from '@/modules/user/models/User'
import { useUserStore } from '@/modules/user/stores/UserStore'
import ApiClient, { FILE_TIMEOUT, PDF_GENERATION_TIMEOUT, SPACE_DUPLICATION_TIMEOUT } from '@/util/ApiClient'
import { defineStore } from 'pinia'
import Vue from 'vue'
import SignaturePositionsResponse from '../models/SignaturePositionsResponse'
import UpdateSpaceBannerRequest from '../models/UpdateSpaceBannerRequest'
import { useTeamStore } from '@/modules/team/stores/TeamStore'
import { DomainValidationStatus } from '@/modules/team/models/Domain'
import { SpaceListColumnCategory } from '@/modules/spacelist/models/SpaceListColumnCategory'
import SpaceListDirectoryColumn from '@/modules/spacelist/models/SpaceListDirectoryColumn'
import EmailRecipientRequest from '@/modules/space/models/EmailRecipientRequest'
import AddRecipientRequest from '../models/AddRecipientRequest'
import VerificationScreenData from '@/modules/space/models/VerificationScreenData'
import { trackCreateSpace, trackOnboardingFlow3 } from '@/util/segment-tracking'
import i18n from '@/util/config/i18n'
import { useAuthStore } from '@/modules/auth/stores/AuthStore'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { useOnboardingStore } from '@/modules/global/stores/OnboardingStore'
import { handlePdfResponse, PdfGenerationResult } from '@/util/space-pdf-finalization-messages'
import { buildCopyName } from '@/util/copyName'
import { getApiErrorStatus } from '@/util/api-error'

/** Latest-wins guard for signature-positions fetches (NS-367 PDF export race). */
let signaturePositionsRequestSeq = 0

type PendingSpaceRename = { confirmedName: string; latestName: string; seq: number; tail: Promise<void> }
/**
 * Per-space rename queue. Renames are sent one at a time, in order, and each compares against the
 * last server-confirmed name rather than the optimistically updated `space.name`, so an explicit commit
 * is never dropped behind an in-flight autosave and an older name can never land last on the server.
 * An entry exists only while a rename for that space is queued or in flight.
 */
const pendingSpaceRenames = new Map<string, PendingSpaceRename>()

export type SignaturePositionsStatus = 'idle' | 'loaded' | 'failed'

export type SpaceState = {
  spaces: Space[]
  spacePages: Record<string, SpacePage[]>
  currentSpaceId: string | null
  currentPageId: string | null
  spaceListDirectoryColumns: SpaceListDirectoryColumn[]
  signaturePositions: SignaturePositionsResponse[]
  signaturePositionsSpaceId: string | null
  signaturePositionsStatus: SignaturePositionsStatus
  triggerPageReload: boolean
}

export const useSpaceStore = defineStore('space', {
  state: (): SpaceState => ({
    spaces: [],
    spacePages: {},
    currentSpaceId: null,
    currentPageId: null,
    spaceListDirectoryColumns: [],
    signaturePositions: [],
    signaturePositionsSpaceId: null,
    signaturePositionsStatus: 'idle',
    triggerPageReload: false
  }),
  getters: {
    activeSpaces(): Space[] {
      return this.spaces.filter(s => !s.isArchived)
    },
    currentSpace(): Space | undefined {
      return this.spaces.find(s => s.id === this.currentSpaceId)
    },
    spacePageByPageId() {
      return (pageId: string) =>
        Object.values(this.spacePages)
          .flatMap(p => p)
          .find(p => p.id === pageId)
    },
    spacePagesBySpaceId() {
      return (spaceId: string) => this.spacePages[spaceId] || []
    },
    spacesCreatedByMe(): Space[] {
      const userStore = useUserStore()
      const currentUserId = userStore.currentUser?.id
      return this.spaces.filter(s => s.createdBy === currentUserId)
    },
    spacesRecentlyViewed(): Space[] {
      return this.spaces.filter(s => s.spaceStatistics.lastViewedByUser)
    },
    archivedSpaces(): Space[] {
      return this.spaces.filter(s => s.isArchived)
    },
    spacesBySpaceListId() {
      return (listId: string) => this.spaces.filter(s => !s.isArchived && s.spaceListId === listId)
    },
    usersToVerifyForSignature(): SignaturePositionsResponse[] {
      const uniqueUserIds = new Set<string>()
      return this.signaturePositions
        .filter(s => !s.signed)
        .filter(s => {
          if (uniqueUserIds.has(s.userId)) {
            return false
          }
          uniqueUserIds.add(s.userId)
          return true
        })
    }
  },
  actions: {
    async getSpaceLink(
      space: Space,
      internal: boolean,
      lang: string = 'en_default',
      useCustomDomain: boolean = true
    ): Promise<string> {
      const teamStore = useTeamStore()
      let baseLink = `${window.location.origin}/${this.getSpaceSlug(space)}` + '?lang=' + lang
      if (useCustomDomain) {
        const customDomain = await teamStore.getCurrentCustomDomain()
        if (customDomain && customDomain.domainStatus === DomainValidationStatus.DOMAIN_ACTIVE) {
          baseLink = `${customDomain.domainName}/${this.getSpaceSlug(space)}` + '?lang=' + lang
        }
      }
      return internal ? `${baseLink}&internal=true` : baseLink
    },
    getSpaceSlug(space: Space) {
      const sluggifiedName = space.name.replaceAll(' ', '-').replace(/[^a-zA-Z0-9-]/g, '')
      return `${sluggifiedName}-${space.id}`
    },
    getIdFromSpaceSlug(spaceSlug: string): string {
      const paramLength = spaceSlug.length
      return spaceSlug.substring(paramLength - 36, paramLength)
    },
    setCurrentSpaceId(spaceId: string | null) {
      if (spaceId !== this.currentSpaceId) {
        this.signaturePositions = []
        this.signaturePositionsSpaceId = null
        this.signaturePositionsStatus = 'idle'
      }
      Vue.set(this, 'currentSpaceId', spaceId)
    },
    setCurrentPageId(pageId: string | null) {
      Vue.set(this, 'currentPageId', pageId)
    },
    setTriggerPageReload(value: boolean) {
      this.triggerPageReload = value
    },
    setSpace(space: Space) {
      const indexToUpdate = this.spaces.findIndex(existing => existing.id === space.id)
      if (indexToUpdate === -1) {
        Vue.set(this, 'spaces', [...this.spaces, space])
        return
      }

      const merged = { ...this.spaces[indexToUpdate], ...space }
      Vue.set(this.spaces, indexToUpdate, merged)
    },
    /** Keeps download UI in sync immediately after a 202 finalization response. */
    markPdfFinalizationPending(spaceId: string) {
      const index = this.spaces.findIndex(space => space.id === spaceId)
      if (index === -1) {
        return
      }
      const current = this.spaces[index].digitalSignatureStatus
      if (
        current === DigitalSignatureStatus.SIGNED ||
        current === DigitalSignatureStatus.PROCESSING ||
        current === DigitalSignatureStatus.PENDING ||
        current === DigitalSignatureStatus.FAILED
      ) {
        return
      }
      Vue.set(this.spaces[index], 'digitalSignatureStatus', DigitalSignatureStatus.PENDING)
    },
    setSpacePages(spaceId: string, pages: SpacePage[]) {
      const sortedPages = pages.length > 1 ? pages.sort((a, b) => a.position - b.position) : pages
      Vue.set(this.spacePages, spaceId, sortedPages)
    },
    setSpacePage(spaceId: string, page: SpacePage) {
      let spacePages = this.spacePages[spaceId] as SpacePage[]
      let oldSpacePage = spacePages.find(p => p.id === page.id)
      if (!oldSpacePage) {
        const sortedPages = [...spacePages, page].sort((a, b) => a.position - b.position)
        Vue.set(this.spacePages, spaceId, sortedPages)
      } else {
        spacePages = spacePages.filter(p => p.id !== page.id)
        oldSpacePage = { ...oldSpacePage, ...page }
        const sortedPages = [...spacePages, oldSpacePage].sort((a, b) => a.position - b.position)
        Vue.set(this.spacePages, spaceId, sortedPages)
      }
    },
    deleteSpacePage(spaceId: string, pageId: string) {
      let spacePages = this.spacePages[spaceId] as SpacePage[]
      spacePages = spacePages.filter(p => p.id !== pageId)
      Vue.set(this.spacePages, spaceId, spacePages)
    },
    async fetchSpaces(url: string, params: Record<string, any> = {}): Promise<void> {
      const response = await ApiClient({
        method: 'get',
        url,
        params
      })

      const spaces = response.data as Space[]
      spaces.forEach(space => {
        this.setSpace(space)
        if (this.spacePages[space.id] == undefined) {
          this.setSpacePages(space.id, [])
        }
      })
    },
    async fetchSpacesCreatedByMe(): Promise<void> {
      await this.fetchSpaces('/spaces/created-by-me')
    },
    async fetchRecentlyViewedSpaces(): Promise<void> {
      await this.fetchSpaces('/spaces/recently-viewed')
    },
    async fetchArchivedSpaces(): Promise<void> {
      await this.fetchSpaces('/spaces/archived')
    },
    async fetchSpacesBySpaceListId(spaceListId: string): Promise<void> {
      await this.fetchSpaces('/spaces', { spaceListId })
    },
    async fetchSpace(spaceId: string): Promise<void> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}`
      })
      const space = response.data as Space
      this.setSpace(space)
    },
    async createSpace(payload: CreateSpaceRequest): Promise<Space> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces`,
        data: payload
      })
      const newSpace = response.data as Space
      this.setSpace(newSpace)
      const userStore = useUserStore()
      const currentUser = userStore.currentUser
      if (currentUser) {
        trackCreateSpace(
          newSpace.id,
          !!payload.spacePageTemplateName,
          payload.spacePageTemplateName!!,
          payload.templateGallery!!,
          !!payload.companyDomain!!
        )
        if (useOnboardingStore().isRegisterFlowAnalytics) {
          trackOnboardingFlow3()
        }
      }
      return newSpace
    },
    async duplicateSpaceAndRoute(duplicateSpace: Space): Promise<Space> {
      const duplicateSpaceRequest = new DuplicateSpaceRequest()
      duplicateSpaceRequest.spaceId = duplicateSpace.id
      duplicateSpaceRequest.name = buildCopyName(
        i18n.t('newspace.copyOf') as string,
        duplicateSpace.name,
        i18n.t('spacelist.untitledSpace') as string,
        this.spacesBySpaceListId(duplicateSpace.spaceListId).map(s => s.name)
      )
      const newSpace = await this.duplicateSpace(duplicateSpaceRequest)
      this.routeToSpace(newSpace)
      return newSpace
    },
    async duplicateSpace(duplicateSpace: DuplicateSpaceRequest): Promise<Space> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${duplicateSpace.spaceId}/duplicate`,
        data: duplicateSpace,
        timeout: SPACE_DUPLICATION_TIMEOUT
      })
      const space = response.data as Space
      this.setSpace(space)
      return space
    },
    forgetSpace(spaceId: string) {
      this.spaces = this.spaces.filter(p => p.id !== spaceId)
      Vue.delete(this.spacePages, spaceId)
      if (this.currentSpaceId === spaceId) {
        this.setCurrentSpaceId(null)
      }
    },
    // Use `{ deferForget: true }` from space detail: forget only after leaving the route.
    async deleteSpace(spaceId: string, options: { deferForget?: boolean } = {}): Promise<void> {
      const { deferForget = false } = options
      try {
        await ApiClient({
          method: 'delete',
          url: `/spaces/${spaceId}`
        })
        if (!deferForget) {
          this.forgetSpace(spaceId)
        }
      } catch (error) {
        // 404: already gone server-side — clear local state unless the caller deferred cleanup.
        if (!deferForget && getApiErrorStatus(error) === 404) {
          this.forgetSpace(spaceId)
        }
        throw error
      }
    },
    async publishSpace(id: string): Promise<Space> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/publish`
      })
      const newSpace = response.data as Space
      this.setSpace(newSpace)
      return newSpace
    },
    async uploadBanner(id: string, file: File): Promise<Space> {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/banner`,
        data: formData,
        timeout: FILE_TIMEOUT
      })
      const newSpace = response.data as Space
      this.setSpace(newSpace)
      return newSpace
    },
    async updateBannerWithBrandfetch(id: string, updateSpaceBannerRequest: UpdateSpaceBannerRequest): Promise<Space> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/banner-brandfetch`,
        data: updateSpaceBannerRequest
      })
      const newSpace = response.data as Space
      this.setSpace(newSpace)
      return newSpace
    },
    async updateSpace(id: string, request: UpdateSpaceRequest): Promise<void> {
      const response = await ApiClient({
        method: 'put',
        url: `/spaces/${id}`,
        data: request
      })
      const updatedSpace = (response.data as GeneralApiResponse<Space>).data
      this.setSpace(updatedSpace)
    },
    // Looks the space up by id on every call: `setSpace` replaces the array slot, so a captured
    // reference would go stale after the PUT response lands.
    setSpaceName(spaceId: string, name: string) {
      const index = this.spaces.findIndex(space => space.id === spaceId)
      if (index !== -1) {
        Vue.set(this.spaces[index], 'name', name)
      }
    },
    /**
     * Optimistically renames a space and persists it through the per-space queue. Resolves `false` when
     * there was nothing to save (or the space is gone), `true` once the server confirmed the name; rejects
     * if the PUT for this call failed. A later queued rename decides the final name, so only the last one
     * rolls back.
     */
    renameSpace(spaceId: string, rawName: string): Promise<boolean> {
      const name = rawName.trim()
      const space = this.spaces.find(s => s.id === spaceId)
      if (!space) {
        return Promise.resolve(false)
      }
      let entry = pendingSpaceRenames.get(spaceId)
      if (!entry) {
        if (space.name === name) {
          return Promise.resolve(false)
        }
        entry = { confirmedName: space.name, latestName: name, seq: 0, tail: Promise.resolve() }
        pendingSpaceRenames.set(spaceId, entry)
      }
      const pending = entry
      const mySeq = ++pending.seq
      pending.latestName = name
      this.setSpaceName(spaceId, name)

      const run = pending.tail.then(async (): Promise<boolean> => {
        try {
          // Deleted or forgotten while this rename waited its turn: nothing left to rename.
          if (!this.spaces.some(s => s.id === spaceId)) {
            return false
          }
          if (pending.confirmedName !== name) {
            await this.updateSpace(spaceId, { name })
            pending.confirmedName = name
          }
          return true
        } catch (error) {
          if (pending.seq === mySeq) {
            this.setSpaceName(spaceId, pending.confirmedName)
          }
          throw error
        } finally {
          if (pending.seq === mySeq) {
            pendingSpaceRenames.delete(spaceId)
          } else {
            this.setSpaceName(spaceId, pending.latestName)
          }
        }
      })
      pending.tail = run.then(
        () => undefined,
        () => undefined
      )
      return run
    },
    async addRecipient(id: string, recipientRequest: AddRecipientRequest): Promise<User | undefined> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/recipients`,
        data: recipientRequest
      })
      const oldSpace = this.spaces.find(space => space.id === id)
      const newSpace = response.data as Space

      const newRecipient = newSpace?.recipients.find(newRecipient => {
        return !oldSpace?.recipients.some(existingRecipient => existingRecipient.userId === newRecipient.userId)
      })

      let user: User | undefined
      if (newRecipient) {
        const userStore = useUserStore()
        try {
          user = await userStore.fetchUser(newRecipient.userId)
        } catch (error) {
          console.error(`Failed to fetch user with ID ${newRecipient.userId}`, error)
        }
      }
      this.setSpace(newSpace)
      return user
    },
    async removeMembers(id: string, removeRecipientsRequest: RemoveRecipientsRequest): Promise<void> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/remove-recipients`,
        data: removeRecipientsRequest
      })
      const space = response.data as Space
      this.setSpace(space)
      // Removing a recipient deactivates their variable assignments server-side, so the cached
      // variables now hold a stale assignedToRecipientId until they are re-fetched.
      await useSpaceVariableStore().fetchVariables(id)
    },
    async getPages(spaceId: string): Promise<SpacePage[]> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/pages`
      })
      const pages = response.data as SpacePage[]
      this.setSpacePages(spaceId, pages)
      return pages
    },
    async createPage(spaceId: string, page: SpacePage): Promise<SpacePage> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${spaceId}/pages`,
        data: page
      })
      const newPage = response.data as SpacePage
      this.setSpacePage(spaceId, newPage)
      return newPage
    },
    async updatePageName(spaceId: string, pageId: string, name: string): Promise<SpacePage> {
      const response = await ApiClient({
        method: 'put',
        url: `/spaces/${spaceId}/pages/${pageId}/name`,
        data: {
          name
        }
      })
      const page = response.data as SpacePage
      this.setSpacePage(spaceId, page)
      return page
    },
    async updatePageSharedStatus(spaceId: string, pageId: string, isShared: boolean): Promise<void> {
      const response = await ApiClient({
        method: 'put',
        url: `/spaces/${spaceId}/pages/${pageId}/shared`,
        data: {
          isShared: isShared
        }
      })
      const page = response.data as SpacePage
      this.setSpacePage(spaceId, page)
    },
    async updatePagePosition(spaceId: string, pageId: string, position: number): Promise<void> {
      const response = await ApiClient({
        method: 'put',
        url: `/spaces/${spaceId}/pages/${pageId}/position`,
        data: {
          position
        }
      })
      const page = response.data as SpacePage
      this.setSpacePage(spaceId, page)
    },
    async duplicatePage(pageId: string, spaceId: string, name: string, position: number): Promise<void> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${spaceId}/pages/${pageId}/duplicate`,
        data: {
          name,
          position
        }
      })
      const page = response.data as SpacePage
      this.setSpacePage(spaceId, page)
    },
    async deletePage(spaceId: string, pageId: string): Promise<void> {
      await ApiClient({
        method: 'delete',
        url: `/spaces/${spaceId}/pages/${pageId}`
      })
      this.deleteSpacePage(spaceId, pageId)
    },
    async setVerificationMode(spaceId: string, verificationMode: SpaceVerificationMode): Promise<void> {
      const response = await ApiClient({
        method: 'put',
        url: `/spaces/${spaceId}/verification-mode`,
        data: { verificationMode }
      })
      const space = response.data as Space
      this.setSpace(space)
    },
    async routeToSpace(space: Space) {
      const router = (await import('@/router')).default
      router.push(`/${this.getSpaceSlug(space)}`)
    },
    async createSpaceAndRoute(payload: CreateSpaceRequest) {
      const createdSpace = await this.createSpace(payload)
      // NS-310: onboarding is done the moment the space exists - recorded before enrichment and
      // navigation, so a failure in either cannot reopen the welcome flow and ask for a second
      // space. A failed creation throws above and correctly leaves onboarding pending.
      const completedOnboarding = useOnboardingStore().completeIfPending()
      if (payload.spacePageTemplateId) {
        try {
          await useSpaceVariableStore().fetchVariables(createdSpace.id)
        } catch (error) {
          if (!completedOnboarding) {
            throw error
          }
          // Onboarding already counted this space, so surfacing this as a failed creation would
          // invite a duplicate. The space view refetches variables on open.
          console.error('Failed to load variables for the newly created space', error)
        }
      }
      this.routeToSpace(createdSpace)
      return createdSpace
    },
    async fetchUsers(spaceId: string): Promise<void> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/users`
      })
      const users = response.data as User[]
      useUserStore().setUsers(users)
    },
    async getSpaceListDirectory() {
      const response = await ApiClient({
        method: 'get',
        url: '/space-list-directories'
      })
      const spaceDirectoryColumns = response.data as SpaceListDirectoryColumn[]
      this.spaceListDirectoryColumns = spaceDirectoryColumns
      return spaceDirectoryColumns
    },
    async updateSpaceListDirectoryVisibility(category: SpaceListColumnCategory, visible: boolean) {
      const response = await ApiClient({
        method: 'put',
        url: `/space-list-directories/${encodeURIComponent(category)}/visibility`,
        params: {
          visible
        }
      })
      const spaceDirectoryColumns = response.data as SpaceListDirectoryColumn[]
      this.spaceListDirectoryColumns = spaceDirectoryColumns
      return spaceDirectoryColumns
    },
    async uploadSellerLogo(id: string, file: File): Promise<Space> {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/seller-logo`,
        data: formData,
        timeout: FILE_TIMEOUT
      })
      const space = response.data as Space
      this.setSpace(space)
      return space
    },
    async uploadClientLogo(id: string, file: File): Promise<Space> {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/client-logo`,
        data: formData,
        timeout: FILE_TIMEOUT
      })
      const space = response.data as Space
      this.setSpace(space)
      return space
    },
    async clearSignaturesOfPage(
      spaceId: string,
      pageId: string,
      options?: { refreshPositions?: boolean }
    ): Promise<Element[]> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${spaceId}/pages/${pageId}/clear-signatures`
      })
      // Apply cleared elements before refreshing positions so a positions failure
      // does not leave ElementStore holding pre-clear signature state.
      const elements = response.data as Element[]
      const elementStore = useElementStore()
      // Only the signature blocks. Writing every key of every returned element re-renders the whole page.
      elements.filter(e => e.type === ElementType.signature).forEach(e => elementStore.mergeElementInPlace(e))
      if (options?.refreshPositions === false) {
        this.signaturePositions = this.signaturePositions.map(p => {
          if (p.pageId !== pageId) {
            return p
          }
          const next = Object.assign(new SignaturePositionsResponse(), p)
          next.signed = false
          return next
        })
      } else {
        this.fetchSpace(spaceId)
        await this.getSignaturePositions(spaceId)
      }
      return elements
    },
    async getSignaturePositions(spaceId: string): Promise<SignaturePositionsResponse[]> {
      const requestId = ++signaturePositionsRequestSeq
      try {
        const response = await ApiClient({
          method: 'get',
          url: `/spaces/${spaceId}/signature-positions`
        })
        const positions = response.data as SignaturePositionsResponse[]
        if (requestId === signaturePositionsRequestSeq) {
          this.signaturePositions = positions
          this.signaturePositionsSpaceId = spaceId
          this.signaturePositionsStatus = 'loaded'
        }
        return positions
      } catch (error) {
        if (requestId === signaturePositionsRequestSeq) {
          this.signaturePositions = []
          this.signaturePositionsSpaceId = null
          this.signaturePositionsStatus = 'failed'
        }
        throw error
      }
    },
    async onSignatureElementUpdated(element: Element): Promise<SignaturePositionsResponse[]> {
      if (element.rootEntityType === RootEntityType.SPACE_PAGE) {
        const page = this.spacePageByPageId(element.rootEntityId)
        if (page !== null) {
          const space = this.spaces.find(s => s.id === page?.spaceId)
          if (space !== null) {
            const [signaturePositions] = await Promise.all([
              this.getSignaturePositions(space!.id),
              this.fetchSpace(space!.id)
            ])
            return signaturePositions
          }
        }
      }
      return []
    },
    async verifyEmail(spaceId: string, email: string): Promise<void> {
      await ApiClient({
        method: 'post',
        url: `/spaces/${spaceId}/verify-email`,
        params: { email }
      })
    },
    async getTasks(spaceId: string) {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/tasks`
      })
      const tasksResult = response.data as Task[]
      const taskStore = useTaskStore()
      tasksResult.forEach(task => taskStore.setTask(task))
      return tasksResult
    },
    /** On failure, callers should use resolvePdfDownloadErrorMessageOrFallback (see space-pdf-finalization-messages). */
    async generatePagePdf(spaceId: string, pageId: string): Promise<PdfGenerationResult> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/pages/${pageId}/pdf`,
        timeout: PDF_GENERATION_TIMEOUT
      })
      return handlePdfResponse(response)
    },
    /** On failure, callers should use resolvePdfDownloadErrorMessageOrFallback (see space-pdf-finalization-messages). */
    async generateSpacePdf(spaceId: string): Promise<PdfGenerationResult> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/pdf`,
        timeout: PDF_GENERATION_TIMEOUT
      })
      return handlePdfResponse(response)
    },
    async isPublic(id: string): Promise<Boolean> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${id}/is-public`
      })
      const isPublic = response.data
      return isPublic
    },
    async canAccessSpace(id: string): Promise<Boolean> {
      // Use getToken() only: tokenCheckGuard already ran isLoggedIn() → checkSpaceToken().
      // Calling isLoggedIn() here ran checkSpaceToken again and could double POST /users/checkToken.
      if (!useAuthStore().getToken()) {
        return false
      }
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${id}/can-access`
      })
      const canAccess = response.data
      return canAccess
    },
    async inviteRecipients(id: string, request: EmailRecipientRequest): Promise<void> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${id}/invite-recipients`,
        data: request
      })
      const space = response.data as Space
      this.setSpace(space)
    },
    async getRecipientPersonalizedLink(spaceId: string, userId: string): Promise<string> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/recipients/${userId}/access-url`
      })
      const link = response.data as string
      return link
    },
    async getVerificationScreenData(spaceId: string): Promise<VerificationScreenData> {
      const response = await ApiClient({
        method: 'get',
        url: `/spaces/${spaceId}/verification-screen-data`
      })
      return response.data as VerificationScreenData
    },
    async finalizeSpacePdf(spaceId: string): Promise<unknown> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${spaceId}/pdf-finalization`,
        timeout: PDF_GENERATION_TIMEOUT
      })
      return response.data
    }
  }
})

export type SpaceStore = ReturnType<typeof useSpaceStore>
